import { Component, HostBinding, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs/internal/Subscription';
import * as _ from 'underscore';
import { cloneDeep } from 'lodash';
import { isArray } from '../../models/utils';
import { Corpus } from '../../models/corpus';
import { Custodian } from '../../models/custodian';
import { DrRequestInfo } from '../../models/dr-request-info';
import { DynamicFolder } from '../../models/dynamic-folder';
import { Endpoint, EntityType, MessageType, UpdateStatusType } from '../../models/enums';
import { ExportStream } from '../../models/export-stream';
import { ObjectActionListType, Permissions } from '../../models/permission';
import { SavedSearch } from '../../models/saved-searches';
import { SearchResult } from '../../models/search-result';
import { View, ViewType } from '../../models/view';
import { Workflow, WorkflowStep } from '../../models/workflow';
import { CommonService } from '../../services/common/common.service';
import { RestService } from '../../services/rest/rest.service';
import { GenericTreeNode } from '../generic-tree/generic-tree-node';
import { GenericTreeComponent } from '../generic-tree/generic-tree.component';
import { XMLService } from '../../services/xml/xml.service';
import { DeferredPromise } from '../../models/deferred-promise';
import { TreeDataLoaderService } from '../../services/tree/treeDataLoader.service';
import { MediaID } from '../../models/mediaid';
import { Batch } from '../../models/batch';

export enum TargetPickerTreeType {
    default = 'default',
    selectSearchTarget = 'selectSearchTarget',
    findNearDuplicates = 'findNearDuplicates',
    findContentDuplicates = 'findContentDuplicates',
    findExactDuplicates = 'findExactDuplicates',
    findMoreLikeThis = 'findMoreLikeThis',
    findMoreLikeThese = 'findMoreLikeThese',
    runAsCombinedSearch = 'runAsCombinedSearch',
    createExportComparisonReport = 'createExportComparisonReport',
    createComparison = 'createComparison',
    searchByProfile = 'searchByProfile',
    selectWorkflowTarget = 'selectWorkflowTarget',
    workflowReport = 'workflowReport'
}

const selectableInfo: ReadonlyMap<EntityType, (pickerType: TargetPickerTreeType, pdView: boolean) => boolean> =
    new Map<EntityType, (pickerType: TargetPickerTreeType, pdView: boolean) => boolean>([
        [EntityType.import, (pickerType, pdView) =>
            pickerType !== TargetPickerTreeType.createExportComparisonReport &&
            pickerType !== TargetPickerTreeType.createComparison &&
            pickerType !== TargetPickerTreeType.searchByProfile &&
            pickerType !== TargetPickerTreeType.selectWorkflowTarget &&
            pickerType !== TargetPickerTreeType.workflowReport &&
            !(pickerType === TargetPickerTreeType.findMoreLikeThese && pdView) &&
            !(pickerType === TargetPickerTreeType.findNearDuplicates && pdView)
        ],
        [EntityType.export, (pickerType) =>
            pickerType !== TargetPickerTreeType.createComparison &&
            pickerType !== TargetPickerTreeType.searchByProfile &&
            pickerType !== TargetPickerTreeType.selectWorkflowTarget
        ],
        [EntityType.projectData, (pickerType) => pickerType !== TargetPickerTreeType.createExportComparisonReport &&
            pickerType !== TargetPickerTreeType.workflowReport
        ],
        [EntityType.custodian, (pickerType) => pickerType !== TargetPickerTreeType.createExportComparisonReport &&
            pickerType !== TargetPickerTreeType.workflowReport
        ],
        [EntityType.mediaID, (pickerType) => pickerType !== TargetPickerTreeType.createExportComparisonReport &&
            pickerType !== TargetPickerTreeType.workflowReport
        ],
        [EntityType.batch, (pickerType) => pickerType !== TargetPickerTreeType.createExportComparisonReport &&
            pickerType !== TargetPickerTreeType.workflowReport
        ],
        [EntityType.tagView, (pickerType) => pickerType !== TargetPickerTreeType.createExportComparisonReport &&
            pickerType !== TargetPickerTreeType.workflowReport
        ],
        [EntityType.folder, (pickerType) => pickerType !== TargetPickerTreeType.createExportComparisonReport &&
            pickerType !== TargetPickerTreeType.workflowReport
        ],
        [EntityType.savedSearch, (pickerType) => pickerType !== TargetPickerTreeType.createExportComparisonReport &&
            pickerType !== TargetPickerTreeType.selectWorkflowTarget &&
            pickerType !== TargetPickerTreeType.workflowReport
        ],
        [EntityType.searchResult, (pickerType) => pickerType !== TargetPickerTreeType.createExportComparisonReport &&
            pickerType !== TargetPickerTreeType.selectWorkflowTarget &&
            pickerType !== TargetPickerTreeType.workflowReport
        ],
        [EntityType.workFlow, (pickerType) => pickerType !== TargetPickerTreeType.createExportComparisonReport &&
            pickerType !== TargetPickerTreeType.selectWorkflowTarget &&
            pickerType !== TargetPickerTreeType.workflowReport
        ],
        [EntityType.discardPile, (pickerType) => pickerType !== TargetPickerTreeType.createExportComparisonReport &&
            pickerType !== TargetPickerTreeType.workflowReport &&
            pickerType !== TargetPickerTreeType.createComparison &&
            pickerType !== TargetPickerTreeType.searchByProfile &&
            pickerType !== TargetPickerTreeType.selectWorkflowTarget
        ]
    ]);

@Component({
    standalone: false,
    selector: 'res-target-picker-tree',
    templateUrl: './target-picker-tree.component.html',
    styleUrls: ['./target-picker-tree.component.scss']
})
export class TargetPickerTreeComponent implements OnInit, OnDestroy {
    @HostBinding('class') classes = 'maximized-component';
    @ViewChild(GenericTreeComponent, { static: true }) tree: GenericTreeComponent;

    @Input() targetPickerTreeType: TargetPickerTreeType;
    @Input() updateStatusType: UpdateStatusType;
    @Input() automationId: string;
    @Input() preselectNodePredicate: (value: GenericTreeNode, index: number, obj: GenericTreeNode[]) => boolean;
    @Input() getSelectedSearchNodeCallback: (searchResultsList: any[]) => any;
    @Input() doubleClickAction: (node: GenericTreeNode) => void;

    nodes: Array<GenericTreeNode> = new Array<GenericTreeNode>();
    nodeMap: Map<EntityType, GenericTreeNode> = new Map<EntityType, GenericTreeNode>();
    selectedNode: GenericTreeNode;
    unfilteredWorkflows: GenericTreeNode[];
    workflowsPulled = false;
    convertedQueries: string[][];

    filterBarVisible = true;
    filterValue = '';
    hideEmptyTargets: boolean;
    hideEmptyTargetVisible = true;
    initialSelectedData = null;
    inputAutoFocused: boolean;
    filteringInProgress: boolean;
    searchLoadingInProgress: boolean;

    private entityChangedEvent: Subscription;
    private pdView: boolean;

    constructor(private commonService: CommonService, private xmlService: XMLService,
        private treeDataService: TreeDataLoaderService, private restService: RestService) {
        this.convertedQueries = new Array<Array<string>>();
        this.entityChangedEvent = this.commonService.getMessage(MessageType.entityChanged).subscribe((data) => {
            const entityType = this.commonService.updateStatusTypeToEntityType(data);
            if (entityType) {
                this.loadData(entityType);
            }
        });
    }

    static isProjectDataView(updateStatusType: UpdateStatusType): boolean {
        return (updateStatusType === UpdateStatusType.WORKING_SET ||
            updateStatusType === UpdateStatusType.CUSTODIAN ||
            updateStatusType === UpdateStatusType.MEDIAID ||
            updateStatusType === UpdateStatusType.BATCH ||
            updateStatusType === UpdateStatusType.TAG_VIEW ||
            updateStatusType === UpdateStatusType.DYNAMIC_FOLDER_VIEW ||
            updateStatusType === UpdateStatusType.COMPARISON);
    }

    ngOnInit(): void {
        if (!this.targetPickerTreeType) {
            this.targetPickerTreeType = TargetPickerTreeType.default;
        }
        this.inputAutoFocused = this.targetPickerTreeType !== TargetPickerTreeType.createComparison;

        this.pdView = TargetPickerTreeComponent.isProjectDataView(this.updateStatusType);
        // import
        if (selectableInfo.get(EntityType.import)(this.targetPickerTreeType, this.pdView) && Permissions.checkUserPermission(ObjectActionListType.CORPUS)) {
            this.nodeMap.set(EntityType.import, new GenericTreeNode({
                name: 'Imports', entityType: EntityType.import, automationId: this.automationId + '-imports',
                isExpandable: true, isSelectable: selectableInfo.get(EntityType.import)(this.targetPickerTreeType, this.pdView),
                requestInfo: new DrRequestInfo({
                    endpoint: Endpoint.corpusSetManager_listCorporaInCorpusSet,
                    responseName: 'corpora',
                    data: { corpusSetHandle: this.commonService.importsHandle }
                }),
                createChild: (data: any): GenericTreeNode => {
                    const item = new Corpus(data);
                    return new GenericTreeNode({
                        name: item.name, entityType: EntityType.import, childPadding: 0, hasExpanderPadding: true, customNumber: item.documentCount,
                        icon: item.smf ? 'fa-comments-o' : 'fa-cube', iconColor: 'color-purple-01',
                        data: item,
                        automationId: 'import-' + item.name
                    });
                },
                data: { name: 'Imports', handle: this.commonService.importsHandle },
            }));
            this.nodes.push(this.nodeMap.get(EntityType.import));
        }
        // export
        if (selectableInfo.get(EntityType.export)(this.targetPickerTreeType, this.pdView) && Permissions.checkUserPermission(ObjectActionListType.EXPORT_STREAM)) {
            this.nodeMap.set(EntityType.export, new GenericTreeNode({
                name: 'Exports', entityType: EntityType.export, automationId: this.automationId + '-exports',
                isExpandable: true, isSelectable: false,
                isExpanded: this.targetPickerTreeType === TargetPickerTreeType.createExportComparisonReport ||
                    this.targetPickerTreeType === TargetPickerTreeType.workflowReport,
                requestInfo: new DrRequestInfo({
                    endpoint: Endpoint.workingSetManager_listExportStreams,
                    responseName: 'exportStreams',
                    data: {
                        handle: this.commonService.projectData?.handle,
                        filters: [
                            { attribute: 'reviewProductions', value: 'false', operator: 'EQUALS' },
                            { attribute: 'reviewStream', value: 'false', operator: 'EQUALS' }
                        ]
                    }
                }),
                createChild: (data: any): GenericTreeNode => {
                    const item = new ExportStream(data);
                    return new GenericTreeNode({
                        data: item,
                        name: item.name,
                        automationId: 'export-stream-' + item.name,
                        entityType: EntityType.export,
                        customNumber: item.documentCount,
                        childPadding: 0,
                        icon: 'fa-cube',
                        iconColor: 'color-blue-01',
                        isExpandable: false,
                        hasExpanderPadding: true
                    });
                },
            }));
            this.nodes.push(this.nodeMap.get(EntityType.export));
        }

        const prjDataVisible = Permissions.checkUserPermission(ObjectActionListType.ECA_CASE_DATA);

        if (prjDataVisible && this.commonService.currentProject) {
            // project data
            const projectData = new GenericTreeNode({
                name: 'Project Data', automationId: this.automationId + '-project-data', entityType: EntityType.projectData,
                isExpanded: true, isExpandable: true,
                isSelectable: selectableInfo.get(EntityType.projectData)(this.targetPickerTreeType, this.pdView),
                data: this.commonService.projectData,
            });

            if (selectableInfo.get(EntityType.custodian)(this.targetPickerTreeType, this.pdView) && Permissions.checkUserPermission(ObjectActionListType.ECA_CUSTODIAN)) {
                this.nodeMap.set(EntityType.custodian, new GenericTreeNode({
                    name: 'Custodians', entityType: EntityType.custodian, automationId: this.automationId + '-project-data-custodians',
                    isExpandable: true, childPadding: 0, isSelectable: false, parent: projectData,
                    requestInfo: new DrRequestInfo({
                        endpoint: Endpoint.ecaManager_listCustodians,
                        responseName: 'custodians',
                        data: { projectHandle: this.commonService.currentProject.handle }
                    }),
                    createChild: (data: any): GenericTreeNode => {
                        const item = new Custodian(data);
                        return new GenericTreeNode({
                            name: item.name, entityType: EntityType.custodian, childPadding: 7.5, hasExpanderPadding: true, customNumber: item.documentCount,
                            icon: item.name === 'Unassigned' ? 'fa-user-circle' : 'fa-user-circle-o', iconColor: item.name === 'Unassigned' ? 'color-grey-08' : 'color-blue-01',
                            data: item,
                            automationId: 'custodian-' + item.name
                        });
                    },
                }));
                projectData.children.push(this.nodeMap.get(EntityType.custodian));
            }
            if (selectableInfo.get(EntityType.mediaID)(this.targetPickerTreeType, this.pdView) && Permissions.checkUserPermission(ObjectActionListType.ECA_MEDIAID)) {
                this.nodeMap.set(EntityType.mediaID, new GenericTreeNode({
                    name: 'MediaIDs', entityType: EntityType.mediaID, automationId: this.automationId + '-project-data-mediaids',
                    isExpandable: true, childPadding: 0, isSelectable: false, parent: projectData,
                    requestInfo: new DrRequestInfo({
                        endpoint: Endpoint.ecaManager_listMediaids,
                        responseName: 'mediaids',
                        data: { projectHandle: this.commonService.currentProject.handle, includeUnassignedMetadataView: true }
                    }),
                    createChild: (data: any): GenericTreeNode => {
                        const item = new MediaID(data);
                        return new GenericTreeNode({
                            name: item.name, entityType: EntityType.mediaID, childPadding: 7.5, hasExpanderPadding: true, customNumber: item.documentCount,
                            icon: 'fa-laptop', iconColor: item.name === 'Unassigned' ? 'color-grey-08' : 'color-blue-01',
                            data: item,
                            automationId: 'mediaid-' + item.name
                        });
                    },
                }));
                projectData.children.push(this.nodeMap.get(EntityType.mediaID));
            }
            if (selectableInfo.get(EntityType.batch)(this.targetPickerTreeType, this.pdView) && Permissions.checkUserPermission(ObjectActionListType.ECA_BATCH)) {
                this.nodeMap.set(EntityType.batch, new GenericTreeNode({
                    name: 'Batches', entityType: EntityType.batch, automationId: this.automationId + '-project-data-batches',
                    isExpandable: true, childPadding: 0, isSelectable: false, parent: projectData,
                    requestInfo: new DrRequestInfo({
                        endpoint: Endpoint.ecaManager_listBatches,
                        responseName: 'batches',
                        data: { projectHandle: this.commonService.currentProject.handle, includeUnassignedMetadataView: true }
                    }),
                    createChild: (data: any): GenericTreeNode => {
                        const item = new Batch(data);
                        return new GenericTreeNode({
                            name: item.name, entityType: EntityType.batch, childPadding: 7.5, hasExpanderPadding: true, customNumber: item.documentCount,
                            icon: 'fa-archive', iconColor: item.name === 'Unassigned' ? 'color-grey-08' : 'color-blue-01',
                            data: item,
                            automationId: 'batch-' + item.name
                        });
                    },
                }));
                projectData.children.push(this.nodeMap.get(EntityType.batch));
            }
            if (selectableInfo.get(EntityType.tagView)(this.targetPickerTreeType, this.pdView) && Permissions.checkUserPermission(ObjectActionListType.PRJ_TAGS)) {
                this.nodeMap.set(EntityType.tagView, new GenericTreeNode({
                    name: 'Tags', entityType: EntityType.tagView, automationId: this.automationId + '-project-data-tags',
                    isExpandable: true, childPadding: 0, isSelectable: false, parent: projectData,
                    requestInfo: new DrRequestInfo({
                        endpoint: Endpoint.workingSetManager_listWorkingSetViews,
                        responseName: 'views',
                        data: {
                            filters: [{ attribute: 'viewType', value: 'TAG', operator: 'EQUALS' }],
                            workingSetHandle: this.commonService.projectData.handle
                        },
                    }),
                    createChild: (data: any): GenericTreeNode => {
                        const item = new View(data);
                        return new GenericTreeNode({
                            name: item.name, entityType: EntityType.tagView, childPadding: 7.5, hasExpanderPadding: true, customNumber: item.documentCount,
                            icon: 'fa-tag', iconColor: item.attributes && item.attributes.length > 0 ? item.attributes.filter(a => a.name === 'displayValue')[0]?.value : null,
                            data: item,
                            automationId: 'tag-' + item.name
                        });
                    }
                }));
                projectData.children.push(this.nodeMap.get(EntityType.tagView));
            }
            if (selectableInfo.get(EntityType.folder)(this.targetPickerTreeType, this.pdView) && Permissions.checkUserPermission(ObjectActionListType.ECA_FOLDER)) {
                this.nodeMap.set(EntityType.folder, new GenericTreeNode({
                    name: 'Folders', entityType: EntityType.folder, automationId: this.automationId + '-project-data-folders',
                    isExpandable: true, childPadding: 0, isSelectable: false, parent: projectData,
                    requestInfo: new DrRequestInfo({
                        endpoint: Endpoint.projectManager_listDynamicFolders,
                        responseName: 'dynamicFolders',
                        data: { projectHandle: this.commonService.currentProject.handle }
                    }),
                    createChild: (data: any): GenericTreeNode => {
                        const item = new DynamicFolder(data);
                        return new GenericTreeNode({
                            name: item.name, entityType: EntityType.folder, childPadding: 7.5, hasExpanderPadding: true, customNumber: item.documentCount,
                            icon: 'fa-folder', iconColor: item.color,
                            data: item,
                            automationId: 'folder-' + item.name
                        });
                    },
                }));
                projectData.children.push(this.nodeMap.get(EntityType.folder));
            }

            if (selectableInfo.get(EntityType.projectData)(this.targetPickerTreeType, this.pdView)) {
                this.nodes.push(projectData);
            }
        }
        // searches
        const searches = new GenericTreeNode({
            name: 'Searches', automationId: this.automationId + '-searches', isExpanded: true,
            isExpandable: true, isSelectable: false
        });
        if (selectableInfo.get(EntityType.savedSearch)(this.targetPickerTreeType, this.pdView) && Permissions.checkUserPermission(ObjectActionListType.SAVED_SEARCH_QUERY)) {
            this.nodeMap.set(EntityType.savedSearch, new GenericTreeNode({
                name: 'Saved Searches', entityType: EntityType.savedSearch, automationId: this.automationId + '-searches-saved-searches',
                isExpandable: true, childPadding: 0, isSelectable: false, parent: searches,
                requestInfo: new DrRequestInfo({
                    endpoint: Endpoint.projectManager_listSavedSearches,
                    responseName: 'activeRules',
                    data: {
                        projectHandle: this.commonService.currentProject?.handle,
                        startIndex: 0,
                        count: 10
                    },
                }),
                createChild: (data: any): GenericTreeNode => {
                    const item = new SavedSearch(data);
                    return new GenericTreeNode({
                        name: item.name,
                        entityType: EntityType.savedSearch,
                        childPadding: 7.5,
                        hasExpanderPadding: true,
                        icon: 'fa-search color-tan-07',
                        customNumber: item.view.documentCount,
                        data: item,
                        automationId: 'saved-search-' + item.handle
                    });
                }
            }));
            searches.children.push(this.nodeMap.get(EntityType.savedSearch));
        }
        if (selectableInfo.get(EntityType.searchResult)(this.targetPickerTreeType, this.pdView)) {
            this.nodeMap.set(EntityType.searchResult, new GenericTreeNode({
                name: 'Search History', entityType: EntityType.searchResult, automationId: this.automationId + '-searches-search-history',
                isExpandable: true, childPadding: 0, isSelectable: false, parent: searches,
                requestInfo: new DrRequestInfo({
                    endpoint: Endpoint.projectManager_listSearchHistory,
                    responseName: 'views',
                    data: {
                        projectHandle: this.commonService.currentProject?.handle,
                        startIndex: 0,
                        count: 10
                    }
                }),
                createChild: (data: any): GenericTreeNode => {
                    const item = new SearchResult(data);
                    return new GenericTreeNode({
                        name: item.name,
                        entityType: EntityType.searchResult,
                        childPadding: 7.5,
                        hasExpanderPadding: true,
                        customNumber: item.documentCount,
                        icon: (item.task !== 'SEARCH_FROM_BULK_SEARCH' && item.task !== 'BULK_SEARCH') ? 'fa-search' : null,
                        iconColor: 'color-tan-07',
                        iconStringContent: (item.task === 'SEARCH_FROM_BULK_SEARCH' || item.task === 'BULK_SEARCH') ? 'B' : null,
                        data: item,
                        automationId: 'search-history-' + item.handle,
                        isDisabled: this.targetPickerTreeType === TargetPickerTreeType.createComparison && (item.viewType === ViewType.CORPUS_SET_RESULT || item.viewType === ViewType.CORPUS_RESULT)
                    });
                },
            }));
            searches.children.push(this.nodeMap.get(EntityType.searchResult));
        }
        if (searches.children.length > 0) {
            this.nodes.push(searches);
        }
        // workflows
        if (selectableInfo.get(EntityType.workFlow)(this.targetPickerTreeType, this.pdView) && Permissions.checkUserPermission(ObjectActionListType.WORK_FLOW)) {
            this.nodeMap.set(EntityType.workFlow, new GenericTreeNode({
                name: 'Workflows', entityType: EntityType.workFlow, automationId: this.automationId + '-workflows',
                isExpandable: true, isSelectable: false,
                requestInfo: new DrRequestInfo({
                    endpoint: Endpoint.workFlowManager_listWorkFlows,
                    responseName: 'workFlows',
                    data: {
                        projectHandle: this.commonService.currentProject?.handle,
                        startIndex: 0
                    }
                }),
                createChild: (data: any): GenericTreeNode => {
                    const item = new Workflow(data);
                    const stepTaskHandles = !data.queryItems ? null : _.filter(_.map(data.queryItems.items, (value: any) => {
                        return value.responseHandle;
                    }), (handle) => handle !== null);
                    const runningAttr = _.find(item.attributes, (attribute) => attribute.name === 'isRunning');
                    const isRunning = runningAttr && runningAttr.value && runningAttr.value === 'true';
                    return new GenericTreeNode({
                        name: item.name,
                        automationId: 'workflow-' + item.name,
                        entityType: EntityType.workFlow,
                        isSelectable: false,
                        childPadding: 0,
                        hasExpanderPadding: true,
                        icon: this.treeDataService.getWorkflowIconByStatus(item),
                        iconColor: 'color-green-07',
                        data: item,
                        isExpandable: true,
                        isExpandedOnSelect: true
                    });
                }
            }));
            this.nodes.push(this.nodeMap.get(EntityType.workFlow));
        }
        if (this.commonService.discardPile) {
            // Discard pile
            if (selectableInfo.get(EntityType.discardPile)(this.targetPickerTreeType, this.pdView) && Permissions.checkUserPermission(ObjectActionListType.DISCARD_PILE)) {
                this.nodeMap.set(EntityType.discardPile, new GenericTreeNode({
                    name: 'Discard Pile', automationId: this.automationId + '-discard-pile', entityType: EntityType.discardPile,
                    isExpandable: false, customClasses: 'node-left-margin',
                    isSelectable: selectableInfo.get(EntityType.discardPile)(this.targetPickerTreeType, this.pdView),
                    icon: 'fa-recycle', iconColor: 'color-grey-10',
                    data: this.commonService.discardPile,

                }));
                this.nodes.push(this.nodeMap.get(EntityType.discardPile));
            }
        }

        setTimeout(() => {
            this.tree.setData(this.nodes);
            this.loadData();
        });
    }

    loadData(entityType?: EntityType): void {
        if (entityType) {
            if (this.nodeMap.has(entityType)) {
                this.loadNodeData(this.nodeMap.get(entityType));
            }
        } else {
            Promise.all(_.map(Array.from(this.nodeMap.values()), node => this.loadNodeData(node)));
        }
    }

    loadNodeData(node: GenericTreeNode): Promise<void> {
        const request = new DeferredPromise<any>();
        if (node && (!node.isLoading || (node.isLoading && this.targetPickerTreeType === TargetPickerTreeType.workflowReport)) && !node.isDisabled && node.isExpandable && node.canLoadData()) {
            node.isLoading = true;
            if (node.entityType === EntityType.workFlow) {
                this.convertedQueries = new Array<Array<string>>();
            }
            this.restService.post(node.requestInfo.endpoint, node.requestInfo.data).then(response => {
                const responseCopy = (JSON.parse(JSON.stringify(response)));
                if (responseCopy && isArray(responseCopy[node.requestInfo.responseName])) {
                    if (node.entityType === EntityType.searchResult) {
                        this.searchLoadingInProgress = false;
                        const list = <Array<any>>responseCopy[node.requestInfo.responseName];
                        if (this.preselectNodePredicate && this.getSelectedSearchNodeCallback && !this.tree.selectedNode) {
                            const searchNodeToSelect = this.getSelectedSearchNodeCallback(list);
                            if (searchNodeToSelect) {
                                list.unshift(searchNodeToSelect);
                            }
                        }
                        if (this.targetPickerTreeType === TargetPickerTreeType.selectSearchTarget && this.initialSelectedData?.handle && !(this.initialSelectedData.target?.data instanceof WorkflowStep)) {
                            const searchResultToSelect = list.find(s => s.handle === this.initialSelectedData.handle);
                            const entityTypeToSelect = this.initialSelectedData.entityType ? this.initialSelectedData.entityType : this.initialSelectedData.target?.entityType;
                            if (!searchResultToSelect && entityTypeToSelect === EntityType.searchResult) {
                                // search result to select is not found in top 10 list => add it as first in the tree
                                list.unshift(this.initialSelectedData);
                            }
                        }
                        if (responseCopy.totalCount > list.length) {
                            list.push(new GenericTreeNode({
                                name: Math.min(10, responseCopy.totalCount - list.length) + ' more ...',
                                hasExpanderPadding: true,
                                childPadding: 20,
                                entityType: node.entityType,
                                isSelectable: false,
                                isButton: true,
                                automationId: 'get-more-searches'
                            }));
                        }
                    }
                    this.tree.setNodeData(node, responseCopy);
                    if (node.entityType === EntityType.workFlow) {
                        this.workflowsPulled = true;
                        this.treeDataService.setWorkflowChildren(node);
                        this.tree.treeDataUpdated();
                        this.moveUnfilteredChildren(false);
                    }

                    if (this.preselectNodePredicate && !this.tree.selectedNode) {
                        const preselectNode = this.tree.getNodeBy(this.preselectNodePredicate);
                        if (preselectNode) {
                            this.tree.selectNode(preselectNode);
                        }
                    }
                }
                node.isLoading = false;
                if (this.filteringInProgress) {
                    this.filteringInProgress = false;
                }
                request.resolve();
            }, error => {
                if (this.filteringInProgress) {
                    this.filteringInProgress = false;
                }
                if (node.entityType === EntityType.searchResult) {
                    this.searchLoadingInProgress = false;
                }
                node.isLoading = false;
                request.reject(error);
            });
        } else { request.reject(); }
        return request.promise;
    }

    moveUnfilteredChildren(toDisplay: boolean): void {
        const workflowParent = _.find(this.nodes, node => node.entityType === EntityType.workFlow);
        workflowParent?.children?.forEach(wfNode => {
            if (toDisplay) {
                wfNode.children = wfNode.data.allChildren;
            } else {
                wfNode.data.allChildren = wfNode.children;
            }
        });
    }

    targetPickerTreefinishLoadingCallback = (node: GenericTreeNode): void => {
        if (node.entityType === EntityType.searchResult && this.targetPickerTreeType === TargetPickerTreeType.createComparison && node.children) {
            if (this.preselectNodePredicate && this.getSelectedSearchNodeCallback && node.children) {
                const childrenDataList = node.children.map(c => c.data);
                const searchData = this.getSelectedSearchNodeCallback(childrenDataList);
                if (searchData) {
                    const treeNodeToSelect = cloneDeep(node.children[0]);
                    treeNodeToSelect.name = searchData.name;
                    treeNodeToSelect.data = searchData;
                    const indexOfLastSeparator = treeNodeToSelect.automationId.lastIndexOf('-');
                    const newAutoId = treeNodeToSelect.automationId.slice(0, indexOfLastSeparator);
                    treeNodeToSelect.automationId = newAutoId + node.children.length;
                    treeNodeToSelect.parent = node;
                    node.children.unshift(treeNodeToSelect);
                    this.tree.treeDataUpdated();
                    this.tree.selectNode(treeNodeToSelect);
                }
            }
        }
    }
    selectedNodeChangedCallback = (node: GenericTreeNode): void => {
        if (node && node.isButton && node.parent && node.parent.requestInfo) {
            node.parent.requestInfo.data.count += 10;
            this.loadData(node.entityType);
        } else {
            this.selectedNode = node;
        }
    }

    filterData(value: string): void {
        this.filterValue = value;
        if (this.filterValue.trim() && this.filterValue.length > 1) {
            this.filteringInProgress = true;
        } else {
            this.filteringInProgress = false;
        }

        const workflowParent = _.find(this.nodes, node => node.entityType === EntityType.workFlow);
        if (workflowParent) {
            workflowParent.loadOnClick = false;
            const optionalcall = (!this.unfilteredWorkflows && !this.workflowsPulled) ? () => Promise.resolve(this.loadNodeData(workflowParent))
                : () => Promise.resolve();

            optionalcall().then(() => {

                if (!this.unfilteredWorkflows) {
                    this.unfilteredWorkflows = cloneDeep(workflowParent.children);
                    this.unfilteredWorkflows.forEach(wfNode => wfNode.isLoading = false);
                }

                if (this.filterValue.length !== 1 && this.filterValue !== '') {
                    this.preselectNodePredicate = null;
                    this.applyFilters();
                }

                if (this.filterValue === '') {
                    const workflowParent = _.find(this.nodes, node => node.entityType === EntityType.workFlow);
                    workflowParent.loadOnClick = true;
                    workflowParent.children = cloneDeep(this.unfilteredWorkflows);
                    this.moveUnfilteredChildren(true);
                    this.tree.treeDataUpdated();
                    this.restoreDefaultTreeNodes()
                }
            });
        } else {
            //code for TargetPickerTreeType.selectWorkflowTarget or TargetPickerTreeType.workflowReport
            if (this.filterValue.length !== 1 && this.filterValue !== '') {
                this.preselectNodePredicate = null;
                this.applyFilters();
            } else {
                this.tree.treeDataUpdated();
                this.restoreDefaultTreeNodes()
            }
        }
    }

    restoreDefaultTreeNodes(): void {
        this.tree.treeControl.collapseAll();
        this.tree.selectedNode = null;
        this.expandNode('Project Data');
        this.expandNode('Searches');
    }

    expandNode(type: string): void {
        const node = _.find(this.nodes, node => node.name === type);

        if (node) {
            this.tree.treeControl.expand(node);
        }
    }

    applyFilters(): void {
        const filterBy = (node: GenericTreeNode) => node.isButton || node.data instanceof Workflow ||
            (
                (this.hideEmptyTargets ? (node.customNumber > 0 || (node.data instanceof Workflow && (node.data as Workflow).resultCount > 0)) : true) &&
                (this.filterValue ? node.name.toLowerCase().includes(this.filterValue) : true)
            );

        Array.from(this.nodeMap.values()).forEach(node => {
            node.filterBy = node.entityType === EntityType.workFlow ? undefined : filterBy;

            if (node.parent && !this.tree.treeControl.isExpanded(node.parent)) {
                this.tree.treeControl.expand(node.parent);
            }
            if (!this.tree.treeControl.isExpanded(node)) {
                this.tree.treeControl.expand(node);
            }
            if (node.entityType === EntityType.searchResult) {
                const newCount = this.filterValue ? 0 : (node.requestInfo.data.count ? node.requestInfo.data.count : 10);
                if (newCount !== node.requestInfo.data.count) {
                    node.requestInfo.data.count = newCount;
                    this.searchLoadingInProgress = true;
                    this.loadData(EntityType.searchResult);
                }
            }
            if (node.entityType === EntityType.workFlow) {
                this.setWorkflowNodeFilter(node);
                this.tree.treeControl.expand(node);
            }
        });
        this.tree.treeDataUpdated();
        this.tree.selectedNode = null;
        this.selectedNode = null;
        if (!this.searchLoadingInProgress) {
            this.filteringInProgress = false;
        }
    }

    hideEmptyTargetsChanged(): void {
        const filterBy = (node: GenericTreeNode) => node.isButton ||
            (
                (this.hideEmptyTargets ? (node.customNumber > 0 || (node.data instanceof Workflow && (node.data as Workflow).resultCount > 0)) : true) &&
                (this.filterValue ? node.name.toLowerCase().includes(this.filterValue) : true)
            );
        Array.from(this.nodeMap.values()).forEach(node => {
            node.filterBy = filterBy;
        });
        this.tree.treeDataUpdated();
        this.tree.selectedNode = null;
        this.selectedNode = null;
    }

    ngOnDestroy(): void {
        this.entityChangedEvent.unsubscribe();
    }

    setWorkflowNodeFilter(node: GenericTreeNode) {
        const filteredWorkflows = [];
        const lowerFilter = this.filterValue.toLowerCase();
        this.unfilteredWorkflows.forEach((wfNode, wfIndex) => {
            let nameMatch = false;
            if (wfNode.name.toLowerCase().indexOf(lowerFilter) > -1) {
                nameMatch = true;
            }

            const filteredSteps = [];
            wfNode.data.allChildren.forEach((queryItem, stepIndex) => {
                let name = this.convertedQueries[wfIndex] ? this.convertedQueries[wfIndex][stepIndex] as string : undefined;
                if (!name) {
                    name = this.xmlService.xmlToQueryConverter(queryItem.data.query) as string;
                    if (!this.convertedQueries[wfIndex]) {
                        this.convertedQueries[wfIndex] = new Array<string>();
                    }
                    this.convertedQueries[wfIndex][stepIndex] = name;
                }
                if ((name as string).toLowerCase().indexOf(lowerFilter) > -1) {
                    filteredSteps.push(queryItem);
                }
            });
            if (filteredSteps.length) {
                wfNode.children = filteredSteps;
                filteredWorkflows.push(wfNode);
            } else if (nameMatch) {
                filteredWorkflows.push(wfNode);
            }

        });
        node.children = filteredWorkflows;
        node.children.forEach(child => this.tree.treeControl.expand(child));
    }
}
